import { useRef, type FC, type RefObject, type MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useFavoritesItem } from 'dibs-portfolio-folder/exports/useFavoritesItem.tsx';
import { SharedFavoriteItem } from '../../components/global/FavoriteIcons/SharedFavoriteItem.tsx';
import {
    BuyerProductTile,
    type ImageBackgroundColor,
    SRC_SET_SIZES_TILE,
} from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { VERTICAL_OFFSET_MOBILE_IMG } from '../../constants/imageConstants.js';
import { useQuickView } from 'dibs-search-product-tile/exports/QuickViewProvider.ts';

import { type SharedMobileRelatedItem_item$key } from './__generated__/SharedMobileRelatedItem_item.graphql.ts';
import { type SharedMobileRelatedItem_user$key } from './__generated__/SharedMobileRelatedItem_user.graphql.ts';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';

type Props = {
    index: number;
    item: SharedMobileRelatedItem_item$key;
    user: SharedMobileRelatedItem_user$key | null | undefined;
    dataTn?: string;
    favorites: FavoritesProviderChildrenProps;
    currency: string;
    wrapperRef?: RefObject<HTMLDivElement | null>;
    itemsPerRow?: number;
    onClick: (e?: MouseEvent) => void;
    onContentVisible?: () => void;
    onContentLoaded?: () => void;
    hideRecentSoldPrice?: boolean;
    isClient: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
};

export const SharedMobileRelatedItem: FC<Props> = ({
    index,
    item: itemRef,
    user: userRef,
    favorites,
    currency,
    wrapperRef,
    itemsPerRow = 2,
    onClick,
    onContentVisible,
    onContentLoaded,
    hideRecentSoldPrice,
    isClient,
    imageBackgroundColor,
}) => {
    const item = useFragment(
        graphql`
            fragment SharedMobileRelatedItem_item on Item
            @argumentDefinitions(priceBookName: { type: "String" }) {
                ...BuyerProductTile_item
                    @arguments(isTrade: $isTrade, priceBookName: $priceBookName, showSeller: false)
                ...useFavoritesItem_item
                ...SharedFavoriteItem_item
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment SharedMobileRelatedItem_user on User {
                ...useFavoritesItem_user
            }
        `,
        userRef
    );

    const tileElementRef = useRef<HTMLDivElement | null>(null);
    const { openQuickView, closeQuickView } = useQuickView();
    const {
        isFolderVisible,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
        showFolderPlusIcon,
    } = useFavoritesItem({
        item,
        user,
        tileElementRef: wrapperRef || tileElementRef,
    });

    return (
        <BuyerProductTile
            viewer={null}
            itemSearch={null}
            item={item}
            user={null}
            afterDisplay={isClient}
            currency={currency}
            imageBackgroundColor={imageBackgroundColor}
            index={index}
            size="small"
            itemsPerRow={itemsPerRow}
            hideRecentSoldPrice={hideRecentSoldPrice}
            onClick={onClick}
            onContentVisible={onContentVisible}
            onContentLoaded={onContentLoaded}
            hasQuickView={!!openQuickView && !!closeQuickView}
            srcSet={{ sizes: SRC_SET_SIZES_TILE }}
            imageLoadVerticalOffset={VERTICAL_OFFSET_MOBILE_IMG}
            showMeasurements={false}
            showMskuSwatch={false}
            showPrice={isClient}
            favoritesComponent={
                <SharedFavoriteItem
                    item={item}
                    favorites={favorites}
                    tileSize="small"
                    isFolderVisible={isFolderVisible}
                    onUnFavorited={onUnFavorited}
                    onFolderActionStart={onFolderActionStart}
                    onFolderActionEnd={onFolderActionEnd}
                    showFolderPlusIcon={showFolderPlusIcon}
                />
            }
            tileElementRef={tileElementRef}
        />
    );
};
